import React from "react";
import { ArrowUpRight, Check, ShieldCheck, ShoppingBag, Play } from "lucide-react";

import { SHOPIFY_APP_STORE_URL } from "./constants";
import { AI_MODEL_LABEL, CASH_IMPACT_LABEL, OPPS_COUNT } from "./aigoMetrics";
import { ImageSlot } from "./ImageSlot";

/**
 * Hero — benefit-led headline (2026-07-06).
 *
 * Current: "Grow revenue and expand margin — on autopilot. Free to start."
 *   → highlights the upside the user gains (revenue growth + margin
 *     expansion + zero-cost entry) instead of the negative framing of
 *     the earlier "can't afford" copy.
 *
 * Alternate headlines to A/B — uncomment ONE at a time to swap:
 *
 *   (A) Number-anchored, urgency-led:
 *       Find your next <span className="text-emerald-600">+$76.8k in profit</span>
 *       — and capture it in one click. <span className="text-emerald-600">Free</span> to start.
 *
 *   (B) Persona-led, autopilot promise:
 *       Your always-on <span className="text-emerald-600">growth operator</span>.
 *       <span className="text-emerald-600">More revenue</span>, higher margin,
 *       zero busywork — <span className="text-emerald-600">free to start</span>.
 */
export const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative pt-12 sm:pt-16 md:pt-20 pb-14 md:pb-20 px-4 sm:px-6 md:px-10"
  >
    <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-8 sm:gap-10 items-center">
      {/* LEFT · copy */}
      <div className="lg:col-span-6 reveal min-w-0 max-w-full overflow-hidden">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-700 font-bold tracking-[0.12em] uppercase mb-5">
          <ShieldCheck className="w-3 h-3" /> Safe by default · you approve every action
        </div>

        <h1
          data-testid="hero-h1"
          className="text-[2.5rem] leading-[1.05] sm:text-5xl md:text-7xl tracking-tight text-slate-900 font-extrabold break-words"
        >
          <span className="text-emerald-600">Grow revenue</span> and{" "}
          <span className="text-emerald-600">expand margin</span> — on autopilot.{" "}
          <br className="hidden md:block" />
          <span className="text-emerald-600">Free</span> to start.
        </h1>

        <p className="mt-5 text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-xl">
          AIGO finds what’s leaking profit, tells you why, and fixes it in <strong className="text-emerald-700">one click</strong> — grounded in your live store data.
        </p>

        {/* Real cash-impact tease */}
        <div
          data-testid="hero-cash-tease"
          className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-xs font-bold shadow-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Example dashboard shows <span className="text-emerald-700 font-extrabold">{CASH_IMPACT_LABEL}</span> across {OPPS_COUNT} opportunities
        </div>

        {/* CTAs */}
        <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <a
            href={SHOPIFY_APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm sm:text-base font-bold shadow-lg shadow-emerald-500/30 transition"
          >
            <ShoppingBag className="w-4 h-4" />
            Start free · connect Shopify
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <a
            href="#overview"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-slate-300 hover:border-emerald-400 hover:text-emerald-700 text-slate-700 text-sm sm:text-base font-semibold transition"
          >
            <Play className="w-4 h-4" /> Watch the 45s demo
          </a>
        </div>

        {/* Trust line — no card, safe, approve */}
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> No card to start</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Safe by default (preview_mode)</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> You approve every action</span>
        </div>

        <div className="mt-3 text-[11px] text-slate-400">{AI_MODEL_LABEL}</div>
      </div>

      {/* RIGHT · Command Center screenshot slot */}
      <div className="lg:col-span-6 reveal min-w-0 max-w-full overflow-hidden" style={{ animationDelay: "180ms" }}>
        <div data-testid="hero-command-shot" className="relative max-w-[92vw] mx-auto">
          <ImageSlot
            slotId="hero"
            label="Command Center · Growth Score + $-Leaks + Approve-all-safe"
            aspect="16/10"
            accent
          />
          {/* Approve-all-safe floating chip */}
          <div
            data-testid="hero-approve-chip"
            className="absolute -bottom-3 -right-2 sm:right-2 md:-right-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900 text-white text-[11px] sm:text-xs font-bold shadow-xl"
            style={{ animation: "ring-pulse 2.4s ease-out infinite" }}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Approve all safe · <span className="text-emerald-400 tabular-nums">{CASH_IMPACT_LABEL}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
